import {
  IntegrationProviders,
  IntegrationTypes,
  OrganizationUserAccessTypes,
} from '@generated/graphql';
import type { Integration } from '@interfaces/integrations';
import { useEffect } from 'react';

export type ZendeskScriptKeys = {
  clientRoleKey: string;
  advisorRoleKey: string;
};

type UserInfoForZendeskScript = {
  userName?: string;
  userEmail?: string;
};

type ZendeskProps = {
  widgetStyle: { color?: string };
  accessType: OrganizationUserAccessTypes;
  user: UserInfoForZendeskScript;
  scriptKeys?: ZendeskScriptKeys;
};

declare global {
  // biome-ignore lint/style/useConsistentTypeDefinitions: override module
  interface Window {
    zE: (...args: any[]) => void;
  }
}

export const getZendeskKeys = (
  integrations: Integration[],
): ZendeskScriptKeys | undefined =>
  integrations.find(
    (integration) =>
      integration.type === IntegrationTypes.ChatAgent &&
      integration.provider === IntegrationProviders.Zendesk,
  )?.configuration;

export const ZendeskWidget = ({
  accessType,
  widgetStyle,
  user,
  scriptKeys,
}: ZendeskProps) => {
  useEffect(() => {
    const zendeskKey =
      accessType === OrganizationUserAccessTypes.Entity
        ? scriptKeys?.clientRoleKey
        : scriptKeys?.advisorRoleKey;

    const script = document.createElement('script');
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
    script.async = true;
    document.head.appendChild(script);

    script.onload = () => {
      if (!window.zE) return;

      window.zE('webWidget', 'identify', {
        name: user.userName,
        email: user.userEmail,
      });

      window.zE('webWidget', 'updateSettings', {
        webWidget: {
          color: {
            theme: widgetStyle.color,
          },
          launcher: {
            mobile: {
              labelVisible: true,
            },
          },
        },
      });
    };

    return () => {
      document.head.removeChild(script);
      if (window.zE) {
        window.zE('webWidget', 'clear');
      }
    };
  }, [accessType, scriptKeys, user, widgetStyle]);

  return null;
};
